import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { authManager } from '../utils/auth';
import { UNLOCK_STATUS_LABEL, UNLOCK_MODE_LABEL, unlockStuckReason } from '../utils/loyalty';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

// The user-detail page's Membership section (somo-circle1-api-admin#199
// Phase 2): locked or unlocked, how they unlocked, the renewal date, the
// auto-unlock setting (somo-circle1-api#793) and their unlock-with-points
// request. CircleOne-only — GET /admin/loyalty/members/:id/membership is
// behind requireNonMmlAdmin — so the page renders this only for those admins.

export const UNLOCKED_HOW_LABEL = {
    points: 'Points',
    points_and_card: 'Points + card',
    card: 'Card',
    family: 'Family plan',
    // Premium with no active subscription record (set outside Stripe, or a
    // missed webhook). Worth a look, not a card payment (API #818 review, L3).
    unknown: 'Unknown (no active subscription record)',
};

const formatDate = (d) => (d
    ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
    : '-');

const Field = ({ label, children }) => (
    <div className="form-group row align-items-center">
        <label className="col-12 col-md-4 col-form-label fwt_600">{label}</label>
        <div className="col-12 col-md-8">{children}</div>
    </div>
);

function renewalText(data) {
    const sub = data.subscription;
    if (data.locked || !sub || data.premiumSource === 'family') return '-';
    if (!sub.currentPeriodEnd) return '-';
    const date = formatDate(sub.currentPeriodEnd);
    return sub.cancelAtPeriodEnd ? `Ends ${date} (won't renew)` : `Renews ${date}`;
}

function MemberUnlockStatus({ memberId }) {
    const [state, setState] = useState({ status: 'loading', data: null });

    useEffect(() => {
        let cancelled = false;
        setState({ status: 'loading', data: null });
        (async () => {
            try {
                const res = await authManager.authenticatedFetch(`${Base_Url}/admin/loyalty/members/${memberId}/membership`);
                const body = await res.json().catch(() => null);
                if (cancelled) return;
                if (res.ok && body?.status === 'success' && body.data) {
                    setState({ status: 'ready', data: body.data });
                } else {
                    setState({ status: 'error', data: null });
                }
            } catch (err) {
                if (process.env.NODE_ENV === 'development') console.error('Failed to load membership:', err);
                if (!cancelled) setState({ status: 'error', data: null });
            }
        })();
        return () => { cancelled = true; };
    }, [memberId]);

    if (state.status === 'loading') return <Field label="Membership"><span className="text-muted">Loading…</span></Field>;
    if (state.status === 'error') return <Field label="Membership"><span className="text-muted">Temporarily unavailable.</span></Field>;

    const { data } = state;
    const { unlock } = data;
    return (
        <>
            <Field label="Membership">
                {data.locked
                    ? <span className="f_red fwt_600">Locked</span>
                    : <span className="f_green fwt_600">Unlocked</span>}
            </Field>
            {!data.locked && (
                <Field label="Unlocked With">{UNLOCKED_HOW_LABEL[data.unlockedHow] || data.unlockedHow || '-'}</Field>
            )}
            <Field label="Renewal">{renewalText(data)}</Field>
            <Field label="Auto-unlock">
                {data.autoUnlock?.enabled ? 'On' : 'Off'}
                <small className="text-muted">
                    {data.autoUnlock?.chosen ? ' (member\'s choice)' : ' (default, member hasn\'t chosen)'}
                </small>
            </Field>
            {unlock && (
                <Field label="Unlock Request">
                    <span>{UNLOCK_MODE_LABEL[unlock.mode] || unlock.mode} · {UNLOCK_STATUS_LABEL[unlock.status] || unlock.status}</span>
                    {unlock.pointsSpent != null && <small className="text-muted"> · {unlock.pointsSpent} pts spent</small>}
                    {unlock.stuck && (
                        <>
                            <br />
                            <span className="badge badge-danger">Stuck</span>
                            {' '}
                            <small className="text-danger">{unlockStuckReason(unlock)}</small>
                            {' '}
                            <Link to="/loyaltyUnlocks" className="f_sz13">See Membership Unlocks</Link>
                        </>
                    )}
                </Field>
            )}
        </>
    );
}

export default MemberUnlockStatus;
